import { FCElement } from '@fc/component';
import { VariantPickEvent, VariantChangeEvent } from '@fc/events';
import { patchDOM } from '@fc/dom-patch';
import { sectionManager } from '@fc/sections';
import { isAbortError, LatestRequestController } from '@fc/section-response';
import { requestYieldCallback } from '@fc/utilities';

export const CARD_RENDER_SECTION_ID = 'section-rendering-product-card';
const CARD_RENDER_SECTION_IDS = new Set([CARD_RENDER_SECTION_ID]);

const MARKET_CONTEXT_QUERY_KEYS = ['country', 'locale'];

function preserveMarketContext(url) {
  const current = new URL(window.location.href);

  for (const key of MARKET_CONTEXT_QUERY_KEYS) {
    const value = current.searchParams.get(key);
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  }

  return url;
}

function usesCardRenderSection(fetchUrl) {
  try {
    const url = new URL(fetchUrl, window.location.origin);
    return CARD_RENDER_SECTION_IDS.has(url.searchParams.get('section_id'));
  } catch {
    return Array.from(CARD_RENDER_SECTION_IDS).some((sectionId) => fetchUrl.includes(`section_id=${sectionId}`));
  }
}

// Manages product variant selection, section fetching, URL sync, and combined listings
export default class VariantPicker extends FCElement {
  #pendingPath;
  #requestController = new LatestRequestController();

  connectedCallback() {
    super.connectedCallback();
    this.addManagedEventListener(this, 'change', this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#requestController.abort();
  }

  #onChange = (evt) => {
    this.variantChanged(evt);
  };

  variantChanged(evt) {
    const target = evt?.target;
    if (!(target instanceof HTMLElement)) return;

    // Resolve the active option element
    const chosenOption = this.resolveChosenOption(target);
    if (!chosenOption) return;

    this.updateSelectedOption(target);

    const optionValueIds = this.getOptionValueIds(chosenOption);
    const currentPath = this.dataset.productUrl?.split('?')[0];
    const linkedPath = chosenOption.dataset.productUrl || chosenOption.dataset.connectedProductUrl;

    this.dispatchEvent(
      new VariantPickEvent({
        id: chosenOption.dataset.optionValueId ?? '',
        mediaId: chosenOption.dataset.optionMediaId ?? '',
        optionValueIds,
        productId: this.dataset.productId ?? '',
        productUrl: (linkedPath || currentPath || '').split('?')[0],
        variantId: chosenOption.dataset.variantId ?? '',
      }),
    );

    const isMainCtx =
      this.dataset.templateProductMatch === 'true' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    const loadNewProduct = isMainCtx && !!linkedPath && linkedPath !== currentPath;

    const fetchUrl = this.buildRequestUrl(chosenOption);
    this.fetchUpdatedSection(fetchUrl, loadNewProduct);

    if (isMainCtx) {
      const historyUrl = this.buildHistoryUrl(linkedPath || currentPath || window.location.pathname, optionValueIds);
      if (historyUrl && historyUrl.href !== window.location.href) {
        requestYieldCallback(() => history.replaceState({}, '', historyUrl.toString()));
      }
    }
  }

  updateSelectedOption(targetElement) {
    // Accept an option-value-id string and resolve to an element
    if (typeof targetElement === 'string') {
      targetElement = this.querySelector(`[data-option-value-id="${targetElement}"]`);
      if (!targetElement) return;
    }
    if (!(targetElement instanceof Element)) return;

    if (targetElement instanceof HTMLOptionElement) {
      const ownerSelect = targetElement.parentElement;
      if (!(ownerSelect instanceof HTMLSelectElement)) return;
      ownerSelect.value = targetElement.value;
      for (const option of ownerSelect.options) option.removeAttribute('selected');
      targetElement.setAttribute('selected', 'selected');
      this.#syncLinkedOptionControls(targetElement);
      return;
    }

    // Radio input
    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
      this.#syncLinkedOptionControls(targetElement);
      return;
    }

    // Select dropdown — sync the selected attribute to match current value
    if (targetElement instanceof HTMLSelectElement) {
      const match = Array.from(targetElement.options).find((o) => o.value === targetElement.value);
      if (!match) return;
      for (const o of targetElement.options) o.removeAttribute('selected');
      match.setAttribute('selected', 'selected');
      this.#syncLinkedOptionControls(match);
    }
  }

  resolveChosenOption(targetElement) {
    if (targetElement instanceof HTMLSelectElement) {
      return targetElement.options[targetElement.selectedIndex] ?? null;
    }

    if (targetElement instanceof HTMLOptionElement || targetElement instanceof HTMLInputElement) {
      return targetElement;
    }

    return null;
  }

  buildRequestUrl(chosenOption, sourceContext = null, sourceOptionValues = []) {
    // Resolve product path (combined-listing aware)
    const path =
      chosenOption.dataset.productUrl ||
      chosenOption.dataset.connectedProductUrl ||
      this.#pendingPath ||
      this.dataset.productUrl;
    if (!path) throw new Error('Product path is required');
    this.#pendingPath = path;

    const params = [];
    const optionValueIds =
      Array.isArray(sourceOptionValues) && sourceOptionValues.length
        ? sourceOptionValues.filter(Boolean)
        : this.getOptionValueIds(chosenOption);

    // Collect option_values depending on context
    if (optionValueIds.length && (!sourceContext || sourceContext === 'product-card')) {
      params.push(`option_values=${optionValueIds.join(',')}`);
    }

    // Quick-add / swatches context uses a fixed section id
    if (this.closest('quick-add') || this.closest('swatch-picker')) {
      const url = preserveMarketContext(new URL(path, window.location.origin));

      url.searchParams.delete('variant');
      url.searchParams.set('section_id', CARD_RENDER_SECTION_ID);
      for (const param of params) {
        const [key, value] = param.split('=');
        if (key && value) url.searchParams.set(key, value);
      }
      return `${url.pathname}${url.search}`;
    }

    const url = preserveMarketContext(new URL(path, window.location.origin));
    url.searchParams.delete('variant');
    for (const param of params) {
      const [key, value] = param.split('=');
      if (key && value) url.searchParams.set(key, value);
    }
    return `${url.pathname}${url.search}`;
  }

  buildHistoryUrl(productPath, optionValueIds = []) {
    if (!productPath) return null;

    const url = new URL(window.location.href);
    url.pathname = productPath.split('?')[0];

    if (optionValueIds.length) {
      url.searchParams.set('option_values', optionValueIds.join(','));
    } else {
      url.searchParams.delete('option_values');
    }

    url.searchParams.delete('variant');
    return url;
  }

  buildLinkedProductUrl(chosenOption = null, optionValueIds = []) {
    const option = this.resolveChosenOption(chosenOption ?? this.selectedOption);
    const currentPath = this.dataset.productUrl?.split('?')[0];
    const productPath = option?.dataset.productUrl || option?.dataset.connectedProductUrl || currentPath;
    const ids = Array.isArray(optionValueIds) && optionValueIds.length ? optionValueIds : this.getOptionValueIds(option);

    return this.buildHistoryUrl(productPath, ids);
  }

  prefetchUpdatedSection(fetchUrl) {
    if (!fetchUrl || window.Shopify?.designMode === true) return Promise.resolve(null);

    return sectionManager.getHTMLDocument(fetchUrl, {
      cache: true,
    }).catch(() => null);
  }

  async fetchUpdatedSection(fetchUrl, shouldMorphEntirePage = false) {
    if (!fetchUrl) return;

    const request = this.#requestController.start();

    try {
      const doc = await this.#getFreshDocument(fetchUrl, request.signal, shouldMorphEntirePage);

      if (!request.isCurrent()) return;

      // Allow overflow-list to render immediately
      doc.querySelector('overflow-row[defer]')?.removeAttribute('defer');

      const freshPicker = this.#findFreshPicker(doc);
      const variantJson =
        freshPicker?.querySelector('script[type="application/json"]')?.textContent ??
        doc.querySelector('variant-select script[type="application/json"], swatch-picker script[type="application/json"]')
          ?.textContent;
      if (!variantJson) return;

      this.#pendingPath = undefined;

      if (shouldMorphEntirePage) {
        this.updateMain(doc);
        return;
      }

      const newProduct = this.updateVariantPicker(doc);
      const optId = this.selectedOptionId;
      if (optId) {
        this.dispatchEvent(
          new VariantChangeEvent(JSON.parse(variantJson), optId, {
            html: doc,
            productId: this.dataset.productId ?? '',
            newProduct,
          }),
        );
      }
    } catch (error) {
      if (!isAbortError(error)) {
        return;
      }

      return;
    } finally {
      request.finish();
    }
  }

  #getFreshDocument(fetchUrl, signal, shouldMorphEntirePage) {
    if (shouldMorphEntirePage) {
      return sectionManager.getHTMLDocument(fetchUrl, {
        cache: false,
        signal,
      });
    }

    if (usesCardRenderSection(fetchUrl)) {
      return sectionManager.getHTMLDocument(fetchUrl, {
        cache: window.Shopify?.designMode !== true,
        signal,
      });
    }

    if (this.dataset.sectionId) {
      return sectionManager.getSectionDocument(this.dataset.sectionId, {
        cache: false,
        signal,
        url: fetchUrl,
      });
    }

    return sectionManager.getHTMLDocument(fetchUrl, {
      cache: false,
      signal,
    });
  }

  updateVariantPicker(freshDoc) {
    const freshEl = this.#findFreshPicker(freshDoc);
    if (!freshEl) throw new Error('Fresh picker element not found');

    let productChange;
    if (freshEl instanceof HTMLElement) {
      const { productId, productUrl } = freshEl.dataset;
      if (productId && productUrl && this.dataset.productId !== productId) {
        productChange = { id: productId, url: productUrl };
      }
      if (productId) this.dataset.productId = productId;
      if (productUrl) this.dataset.productUrl = productUrl;
    }

    patchDOM(this, freshEl);

    return productChange;
  }

  updateMain(freshDoc) {
    const currentMain = document.querySelector('main');
    const freshMain = freshDoc.querySelector('main');
    if (!(currentMain instanceof HTMLElement) || !(freshMain instanceof HTMLElement)) {
      throw new Error('Main elements not found');
    }
    patchDOM(currentMain, freshMain);
  }

  get selectedOption() {
    const el = this.querySelector('select option[selected], fieldset input:checked');
    if (el instanceof HTMLInputElement || el instanceof HTMLOptionElement) return el;
    return undefined;
  }

  get selectedOptionId() {
    return this.selectedOption?.dataset.optionValueId ?? undefined;
  }

  get selectedOptionsValues() {
    return this.getOptionValueIds();
  }

  #findFreshPicker(sourceRoot) {
    const tag = this.tagName.toLowerCase();
    const blockId = this.dataset.blockId;

    const candidates = Array.from(sourceRoot.querySelectorAll(tag));
    if (blockId) {
      const blockMatch = candidates.find(
        (candidate) => candidate instanceof HTMLElement && candidate.dataset.blockId === blockId,
      );
      if (blockMatch) return blockMatch;
    }

    return sourceRoot.querySelector(tag);
  }

  getOptionValueIds(targetElement = null) {
    const valuesByPosition = new Map();

    Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).forEach((el) => {
      this.#setOptionValue(valuesByPosition, el);
    });

    const overrideElement = this.resolveChosenOption(targetElement);
    if (overrideElement) {
      this.#setOptionValue(valuesByPosition, overrideElement);
    }

    return Array.from(valuesByPosition.values()).filter(Boolean);
  }

  #setOptionValue(valuesByPosition, element) {
    const optionValueId = element?.dataset?.optionValueId;
    if (!optionValueId) return;

    const optionPosition = this.#resolveOptionPosition(element);
    valuesByPosition.set(optionPosition, optionValueId);
  }

  #resolveOptionPosition(element) {
    return element?.dataset?.fitOptionPosition || element?.dataset?.inputId?.split('-')?.[0] || element?.dataset?.optionValueId;
  }

  #syncLinkedOptionControls(sourceElement) {
    const optionValueId = sourceElement.dataset?.optionValueId;
    const optionPosition = this.#resolveOptionPosition(sourceElement);
    if (!optionValueId || !optionPosition) return;

    const select = this.querySelector(`select[data-fit-option-position="${optionPosition}"]`);
    if (select instanceof HTMLSelectElement) {
      const match = Array.from(select.options).find((option) => option.dataset.optionValueId === optionValueId);
      if (match) {
        select.value = match.value;
        for (const option of select.options) option.removeAttribute('selected');
        match.setAttribute('selected', 'selected');
      }
    }

    const proxyInputs = this.querySelectorAll(
      `input[data-fit-mobile-proxy="true"][data-fit-option-position="${optionPosition}"]`,
    );
    proxyInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.dataset.optionValueId === optionValueId;
      }
    });
  }
}

// Register custom element
if (!customElements.get('variant-select')) {
  customElements.define('variant-select', VariantPicker);
}
